import { useEffect, useState } from "react";
import { Share2, Copy, ExternalLink, Loader2, Globe, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";

export default function StatusPageManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [slug, setSlug] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [selected, setSelected] = useState([]);
  const [webapps, setWebsites] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [sp, ws] = await Promise.all([api.get("/status-page"), api.get("/websites")]);
        setTitle(sp.data.title || "");
        setDescription(sp.data.description || "");
        setSlug(sp.data.slug || "");
        setIsPublic(!!sp.data.is_public);
        setSelected(sp.data.website_ids || []);
        setWebsites(ws.data);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (id) =>
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/status-page", {
        title, description, slug, is_public: isPublic, website_ids: selected,
      });
      setSlug(data.slug);
      toast.success("Status page saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = slug ? `${window.location.origin}/status/${slug}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Public link copied");
  };

  if (loading) {
    return (
      <div className="max-w-3xl space-y-6" data-testid="status-page-skeleton">
        <Skeleton className="h-8 w-48" />
        <Card className="border-slate-200 p-6"><Skeleton className="h-40 w-full" /></Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-8" data-testid="status-page-manager">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Status Page</h1>
        <p className="mt-1 text-sm text-slate-500">Share a public page so anyone can see your services' health.</p>
      </div>

      <Card className="border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="rounded-lg bg-blue-50 p-2 text-blue-600"><Share2 size={18} /></span>
            <div>
              <p className="text-sm font-semibold text-slate-900">Public status page</p>
              <p className="text-xs text-slate-500">{isPublic ? "Anyone with the link can view" : "Currently private"}</p>
            </div>
          </div>
          <Switch checked={isPublic} onCheckedChange={setIsPublic} data-testid="status-public-toggle" />
        </div>

        {slug && (
          <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <span className="flex-1 truncate font-mono text-xs text-slate-600" data-testid="status-public-url">{publicUrl}</span>
            <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-status-link"><Copy size={14} className="mr-1.5" /> Copy</Button>
            <Button variant="outline" size="sm" onClick={() => window.open(publicUrl, "_blank")} disabled={!isPublic}><ExternalLink size={14} className="mr-1.5" /> Open</Button>
          </div>
        )}
      </Card>

      <Card className="border-slate-200 p-6 space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Page title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="status-title-input" placeholder="Acme Status" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Description (optional)</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="status-desc-input" placeholder="Current status of our services" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">URL slug</Label>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-slate-400">/status/</span>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="status-slug-input" placeholder="acme" className="font-mono" />
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 p-6">
        <p className="mb-1 text-sm font-semibold text-slate-900">Monitors to display</p>
        <p className="mb-4 text-xs text-slate-500">{selected.length} of {webapps.length} selected</p>
        {webapps.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-400">Add some webapps first.</p>
        ) : (
          <div className="space-y-1">
            {webapps.map((w) => (
              <label key={w.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 hover:bg-slate-50 cursor-pointer transition-colors" data-testid={`status-site-${w.id}`}>
                <Checkbox checked={selected.includes(w.id)} onCheckedChange={() => toggle(w.id)} />
                <Globe size={15} className="text-slate-400" />
                <span className="flex-1 truncate text-sm text-slate-800">{w.name}</span>
                <span className="font-mono text-xs text-slate-400">{w.status}</span>
              </label>
            ))}
          </div>
        )}
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-status-page-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
          {saving ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Check size={15} className="mr-1.5" />} Save status page
        </Button>
      </div>
    </div>
  );
}
